import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  signal,
  OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, tap } from 'rxjs';
import { SharedModule } from '~backoffice/app/~shared/modules/shared.module';
import { enumToArray } from '~backoffice/app/~shared/utils/utils';
import { ResourceFacade } from '~backoffice/app/~store/facades/resource.facade';
import { Tag, TagAlgorithm, TagCreationParams } from '~core/models/core.models';
import { DialogService } from '~core/services/dialog.service';
import { TagService } from '~core/services/tag.service';
import { GlobalFacade } from '~backoffice/app/~store/facades/global.facade';
import { SnackbarService } from '~backoffice/app/~shared/services/snackbar.service';

@Component({
  selector: 'edit-tag',
  imports: [SharedModule],
  standalone: true,
  templateUrl: './edit-tag.component.html',
  styleUrl: './edit-tag.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditTagComponent implements OnInit {
  private _dialogService = inject(DialogService);
  private _globalFacade = inject(GlobalFacade);
  private _cdRef = inject(ChangeDetectorRef);
  private _tagService = inject(TagService);
  private _resourceFacade = inject(ResourceFacade);
  private _destroyRef = inject(DestroyRef);
  private _snackbarService = inject(SnackbarService);

  tag = input<Tag | null | undefined>();

  isLoading = signal<boolean>(false);
  isSubmitting = signal(false);

  form = new FormGroup({
    title: new FormControl<string>('', [
      Validators.required,
      Validators.minLength(2)
    ]),
    algorithmId: new FormControl<string | undefined>(TagAlgorithm.default)
  });

  algorithms = enumToArray(TagAlgorithm);

  violations$ = this._globalFacade.formViolations$.pipe(
    tap({
      next: violations => {
        setTimeout(() => {
          violations?.forEach(violation =>
            this.form.get(violation.propertyPath)?.setErrors({
              httpError: violation.title
            })
          );
          this._cdRef.markForCheck();
        }, 500);
      }
    })
  );

  ngOnInit(): void {
    const tag = this.tag();
    if (tag) {
      this.patchFormValues(tag);
    }
  }

  patchFormValues(tag: Tag | null | undefined): void {
    if (tag) {
      this.form.patchValue({
        title: tag.title,
        algorithmId: tag.algorithmId
      });
      this._cdRef.detectChanges();
    }
  }

  onSubmit(): void {
    this.isSubmitting.set(true);
    const tagId = this.tag()?.id;
    if (tagId) {
      this.updateTag();
    } else {
      this.createTag();
    }
  }

  createTag(): void {
    const formValue = this.form.value as TagCreationParams;
    this.isLoading.set(true);
    this._tagService
      .create(formValue)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._dialogService.closeAll();
            this._snackbarService.open({
              text: 'Tag créé avec succès',
              type: 'success'
            });
            this._resourceFacade.getData({ refresh: true });
          }
        }),
        finalize(() => {
          this.isLoading.set(false);
          this.isSubmitting.set(false);
          this._cdRef.detectChanges();
        })
      )
      .subscribe();
  }

  updateTag(): void {
    const formValue = this.form.value as TagCreationParams;

    const tag = this.tag();
    if (!tag?.id) return;

    this.isLoading.set(true);

    this._tagService
      .update(tag.id, formValue)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._dialogService.closeAll();
            this._snackbarService.open({
              text: 'Tag modifié avec succès',
              type: 'success'
            });
            this._resourceFacade.getData({ refresh: true });
          }
        }),
        finalize(() => {
          this.isLoading.set(false);
          this.isSubmitting.set(false);
          this._cdRef.detectChanges();
        })
      )
      .subscribe();
  }

  get modalTitle(): string {
    return this.tag() ? `Modification du tag` : `Créer un nouveau tag`;
  }

  get modalDescription(): string {
    return this.tag()
      ? `Veuillez éditer les champs ci-dessous pour mettre à jour le tag`
      : `Veuillez remplir les champs ci-dessous pour la création d'un nouveau tag`;
  }
}
